import React from 'react';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const GoldDiamondLogo = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <polygon points="24,4 44,16 24,44 4,16" fill="#FFD700" stroke="#FFA500" strokeWidth="2" />
    <polygon points="24,4 34,16 24,44 14,16" fill="#FFF8DC" stroke="#FFA500" strokeWidth="2" />
  </svg>
);

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 50%, #A5D6A7 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Poppins', sans-serif",
    position: 'relative',
    overflow: 'hidden',
  },
  backgroundPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 80%, rgba(0, 200, 83, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 200, 83, 0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 24,
    boxShadow: '0 20px 60px rgba(0, 200, 83, 0.15), 0 8px 32px rgba(0, 200, 83, 0.1)',
    padding: '3rem 2.5rem',
    maxWidth: 420,
    width: '100%',
    textAlign: 'center',
    border: '1px solid rgba(0, 200, 83, 0.2)',
    backdropFilter: 'blur(20px)',
    position: 'relative',
    zIndex: 2,
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 1.5,
    gap: 12,
  },
  logo: {
    fontSize: 48,
    color: '#00C853',
    filter: 'drop-shadow(0 4px 8px rgba(0, 200, 83, 0.3))',
  },
  title: {
    fontWeight: 800,
    fontSize: 32,
    color: '#1B5E20',
    marginBottom: 8,
    letterSpacing: 1.5,
    textShadow: '0 2px 4px rgba(27, 94, 32, 0.2)',
  },
  subtitle: {
    color: '#2E7D32',
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 2.5,
    opacity: 0.8,
  },
  form: {
    marginTop: 2,
  },
  inputGroup: {
    position: 'relative',
    marginBottom: 1.5,
  },
  label: {
    display: 'block',
    textAlign: 'left',
    marginBottom: 8,
    color: '#1B5E20',
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: 0.5,
  },
  inputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '16px 20px 16px 50px',
    border: '2px solid #E8F5E9',
    borderRadius: 12,
    fontSize: 16,
    outline: 'none',
    background: '#fff',
    color: '#1B5E20',
    fontFamily: 'inherit',
    fontWeight: 500,
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
  inputFocus: {
    border: '2px solid #00C853',
    boxShadow: '0 0 0 4px rgba(0, 200, 83, 0.1)',
    background: '#F1F8E9',
  },
  inputIcon: {
    position: 'absolute',
    left: 16,
    color: '#00C853',
    fontSize: 20,
    zIndex: 1,
  },
  passwordToggle: {
    position: 'absolute',
    right: 16,
    color: '#00C853',
    cursor: 'pointer',
    padding: 4,
    borderRadius: 4,
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(0, 200, 83, 0.1)',
    },
  },
  rememberForgotRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '1.2rem',
    marginBottom: '1.2rem',
  },
  rememberLabel: {
    display: 'flex',
    alignItems: 'center',
    color: '#1B5E20',
    fontWeight: 500,
    fontSize: 14,
    cursor: 'pointer',
    userSelect: 'none',
  },
  checkbox: {
    marginRight: 8,
    accentColor: '#00C853',
    transform: 'scale(1.1)',
  },
  forgotLink: {
    color: '#00C853',
    fontSize: 14,
    textDecoration: 'none',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    fontWeight: 600,
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#1B5E20',
      textDecoration: 'underline',
    },
  },
  button: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 18,
    cursor: 'pointer',
    marginTop: 1,
    boxShadow: '0 4px 16px rgba(0, 200, 83, 0.3)',
    letterSpacing: 0.5,
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
      boxShadow: '0 6px 20px rgba(0, 200, 83, 0.4)',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      background: '#C8E6C9',
      color: '#A5D6A7',
      boxShadow: 'none',
      transform: 'none',
      cursor: 'not-allowed',
    },
  },
  buttonRipple: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    pointerEvents: 'none',
  },
  error: {
    color: '#D32F2F',
    marginTop: 1,
    fontWeight: 600,
    fontSize: 14,
    background: '#FFEBEE',
    padding: '12px 16px',
    borderRadius: 8,
    border: '1px solid #FFCDD2',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  success: {
    color: '#2E7D32',
    marginTop: 1,
    fontWeight: 600,
    fontSize: 14,
    background: '#E8F5E9',
    padding: '12px 16px',
    borderRadius: 8,
    border: '1px solid #C8E6C9',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  loadingSpinner: {
    display: 'inline-block',
    width: 16,
    height: 16,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid #fff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Add CSS animation for loading spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

class LoginForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      focus: {},
      showPassword: false,
    };
  }

  handleFocus = (e) => {
    this.setState({ focus: { ...this.state.focus, [e.target.name]: true } });
  };

  handleBlur = (e) => {
    this.setState({ focus: { ...this.state.focus, [e.target.name]: false } });
  };

  togglePasswordVisibility = () => {
    this.setState(prevState => ({ showPassword: !prevState.showPassword }));
  };

  render() {
    const { username, password, onChange, onSubmit, loading, error, user } = this.props;
    const { focus, showPassword } = this.state;

    return (
      <div style={styles.container}>
        <div style={styles.backgroundPattern}></div>
        <div style={styles.card}>
          <div style={styles.logoContainer}>
            <GoldDiamondLogo />
            <div>
              <div style={styles.title}>JewellERP</div>
              <div style={styles.subtitle}>Enterprise Resource Planning</div>
            </div>
          </div>
          
          <form onSubmit={onSubmit} autoComplete="on" style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Username</label>
              <div style={styles.inputContainer}>
                <LockIcon style={styles.inputIcon} />
                <input
                  style={{ ...styles.input, ...(focus.username ? styles.inputFocus : {}) }}
                  type="text"
                  name="username"
                  value={username}
                  onChange={onChange}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                  required
                  autoComplete="username"
                  placeholder="Enter your username"
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.inputContainer}>
                <LockIcon style={styles.inputIcon} />
                <input
                  style={{ ...styles.input, ...(focus.password ? styles.inputFocus : {}) }}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={onChange}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                  required
                  autoComplete="current-password"
                  placeholder="Enter your password"
                />
                <div 
                  style={styles.passwordToggle}
                  onClick={this.togglePasswordVisibility}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(0, 200, 83, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                  }}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </div>
              </div>
            </div>

            <div style={styles.rememberForgotRow}>
              <label style={styles.rememberLabel}>
                <input type="checkbox" style={styles.checkbox} />
                Remember me
              </label>
              <button 
                type="button" 
                style={styles.forgotLink} 
                onClick={() => alert('Forgot password functionality coming soon!')}
                onMouseEnter={(e) => {
                  e.target.style.color = '#1B5E20';
                  e.target.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#00C853';
                  e.target.style.textDecoration = 'none';
                }}
              >
                Forgot password?
              </button>
            </div>

            <button 
              style={styles.button} 
              type="submit" 
              disabled={loading}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.background = 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)';
                  e.target.style.boxShadow = '0 6px 20px rgba(0, 200, 83, 0.4)';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.background = 'linear-gradient(135deg, #00C853 0%, #43A047 100%)';
                  e.target.style.boxShadow = '0 4px 16px rgba(0, 200, 83, 0.3)';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              <div style={styles.buttonRipple}></div>
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <div style={styles.loadingSpinner}></div>
                  Logging in...
                </span>
              ) : (
                'Login'
              )}
            </button>

            {error && (
              <div style={styles.error}>
                ⚠ {error}
              </div>
            )}
            
            {user && (
              <div style={styles.success}>
                ✓ Welcome, {user.username}!
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }
}

export default LoginForm;
